import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import api from '@/lib/axiosInstance';
import Helper from '@/lib/helper';
import { ArrowLeft, Download, History } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

type DocumentSigner = {
    id: number;
    name: string;
    email: string;
    role: string;
    status: string;
    sentAt: string | null;
    viewedAt: string | null;
    completedAt: string | null;
};

type DocumentPayload = {
    id: number;
    listingId: number | null;
    documentTemplateId: number;
    envelopeId: string | null;
    status: number | string | null;
    completedAt: string | null;
    createdAt: string;
    html: string;
    path: string | null;
    listing: { id: number; listingReferenceNumber: string | null; headline: string | null } | null;
    signers: DocumentSigner[];
};

const TEMPLATE_NAMES: Record<number, string> = {
    1: 'General Authorization to sell',
    2: 'General Authorization to let',
    3: 'Agreement to purchase',
    4: 'Agreement to rent',
    5: 'Letter of undertaking',
    6: 'Exclusive authorization to sell',
    7: 'Exclusive authorization to rent',
    8: 'Co-Agency agreement',
};

const templateName = (id: number) => TEMPLATE_NAMES[id] || 'Document';

const stripCodeBlocks = (html = '') =>
    html
        // Remove <code>...</code> tags AND their inner content entirely
        .replace(/<code\b[^>]*>[\s\S]*?<\/code>/gi, '')
        // Remove HTML-encoded &lt;code&gt;...&lt;/code&gt; and their content
        .replace(/&lt;code\b[^&]*&gt;[\s\S]*?&lt;\/code&gt;/gi, '');

const DocumentViewPage = () => {
    const navigate = useNavigate();
    const { documentId } = useParams<{ documentId: string }>();
    const [doc, setDoc] = useState<DocumentPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [showTimeline, setShowTimeline] = useState(false);

    const fetchDocument = useCallback(async () => {
        if (!documentId) return;
        try {
            setLoading(true);
            const response = await api.get(`/me/documents/by-id/${documentId}`);
            if (!response.data.status) throw new Error(response.data.error);
            setDoc(response.data.document);
        } catch (err) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setLoading(false);
        }
    }, [documentId]);

    useEffect(() => {
        fetchDocument();
    }, [fetchDocument]);

    const handleDownload = async () => {
        if (!documentId) return;
        try {
            setDownloading(true);
            const response = await api.get(`/me/documents/by-id/${documentId}/pdf`, {
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = `${doc ? templateName(doc.documentTemplateId).toLowerCase().replace(/[^a-z0-9]+/g, '_') : 'document'}_${documentId}.pdf`;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setDownloading(false);
        }
    };

    const stateLabel = useMemo(() => {
        if (!doc) return 'Pending';
        const normalized = String(doc.status || '').toLowerCase();
        if (normalized === 'completed' || doc.completedAt || String(doc.status) === '1') return 'Completed';
        return 'Pending';
    }, [doc]);

    const completedCount = doc?.signers.filter(s => ['completed', 'signed'].includes(String(s.status).toLowerCase())).length || 0;
    const totalCount = doc?.signers.length || 0;

    const signerStatusBadge = (status: string) => {
        const normalized = String(status || '').toLowerCase();
        if (normalized === 'completed' || normalized === 'signed') {
            return <Badge variant='outline' className='border-green-300 bg-green-50 text-green-700'>Completed</Badge>;
        }
        if (['sent', 'delivered', 'viewed'].includes(normalized)) {
            return <Badge variant='outline' className='border-amber-300 bg-amber-50 text-amber-700 capitalize'>{normalized}</Badge>;
        }
        return <Badge variant='outline' className='border-slate-300 bg-slate-50 text-slate-700 capitalize'>{normalized || 'created'}</Badge>;
    };

    const previewHtml = useMemo(() => {
        if (!doc?.html) return '';
        const cleaned = stripCodeBlocks(doc.html);
        return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
            html, body { margin: 0; padding: 0; background: #fff; overflow: hidden; }
            body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #1f2937; padding: 24px; }
            img { max-width: 100%; height: auto; }
        </style></head><body>${cleaned}</body></html>`;
    }, [doc?.html]);

    // Dynamically size iframe to its content so the outer page scrolls, not the iframe
    const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
        const iframe = e.currentTarget;
        try {
            const body = iframe.contentDocument?.body;
            const html = iframe.contentDocument?.documentElement;
            if (body && html) {
                const height = Math.max(body.scrollHeight, html.scrollHeight);
                iframe.style.height = `${height}px`;
            }
        } catch {
            iframe.style.height = '960px';
        }
    };

    return (
        <div className='pb-8'>
            {/* Header */}
            <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-start gap-2'>
                    <Button asChild type='button' size='icon' variant='ghost' className='mt-0.5 w-8 text-slate-500'>
                        <Link to='/documents' aria-label='Back to documents'>
                            <ArrowLeft className='h-4 w-4 text-black' />
                        </Link>
                    </Button>
                    <div>
                        <h5 className='h5 bold'>Document View</h5>
                        <p className='mt-0.5 text-xs text-slate-400'>
                            {loading ? 'Loading…' : doc ? templateName(doc.documentTemplateId) : 'Document'}
                        </p>
                    </div>
                </div>
                <div className='flex items-center gap-1'>
                    <Button type='button' size='icon' variant='ghost' className='w-8 text-slate-500' onClick={() => setShowTimeline(true)} disabled={!doc} title='Signing timeline'>
                        <History className='h-4 w-4' />
                    </Button>
                    <Button type='button' size='icon' variant='ghost' className='w-8 text-slate-500' onClick={handleDownload} disabled={!doc || downloading} title={downloading ? 'Preparing PDF' : 'Download PDF'}>
                        {downloading ? <Spinner /> : <Download className='h-4 w-4' />}
                    </Button>
                </div>
            </div>
            
            {loading ? (
                <div className='mb-4 flex gap-3'>
                    <Skeleton className='h-5 w-32 rounded' />
                    <Skeleton className='h-5 w-24 rounded' />
                    <Skeleton className='h-5 w-28 rounded' />
                </div>
            ) : doc ? (
                <div className='mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-slate-100 pb-3 text-xs text-slate-400'>
                    <span>{doc.listing?.listingReferenceNumber || '—'}</span>
                    <Badge variant='outline' className={`text-[11px] ${stateLabel === 'Completed' ? 'border-green-300 bg-green-50 text-green-700' : 'border-amber-300 bg-amber-50 text-amber-700'}`}>
                        {stateLabel}
                    </Badge>
                    <span>{completedCount}/{totalCount} signed</span>
                    <span>Created {Helper.formatDateTime(doc.createdAt)}</span>
                </div>
            ) : (
                <div className='rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center'>
                    <p className='text-sm text-slate-500'>Document not found.</p>
                    <Button type='button' size='sm' variant='outline' className='mt-4' onClick={() => navigate('/documents')}>
                        Back to Documents
                    </Button>
                </div>
            )}

            {/* Document preview */}
            {doc && (
                <div className='mx-auto w-full max-w-[920px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm'>
                    {previewHtml ? (
                        <iframe
                            title='Document preview'
                            srcDoc={previewHtml}
                            scrolling='no'
                            className='block w-full'
                            style={{ minHeight: '960px', overflow: 'hidden' }}
                            onLoad={handleIframeLoad}
                        />
                    ) : (
                        <div className='flex min-h-[480px] items-center justify-center p-8 text-center text-sm text-slate-500'>
                            Document HTML is not available.
                        </div>
                    )}
                </div>
            )}

            {/* Timeline sheet */}
            <Sheet open={showTimeline} onOpenChange={setShowTimeline}>
                <SheetContent className='w-full overflow-y-auto sm:max-w-sm p-4'>
                    <SheetHeader>
                        <SheetTitle className='text-sm font-semibold'>Signing Timeline</SheetTitle>
                    </SheetHeader>
                    <div className='mt-4 space-y-3'>
                        {doc?.signers?.length ? (
                            doc.signers.map((signer) => (
                                <div key={signer.id} className='rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5'>
                                    <div className='flex items-center justify-between gap-2'>
                                        <div className='min-w-0'>
                                            <p className='text-sm font-medium text-slate-800 truncate'>{signer.name || '—'}</p>
                                            <p className='text-xs text-slate-400 truncate'>{signer.email}</p>
                                        </div>
                                        {signerStatusBadge(signer.status)}
                                    </div>
                                    <div className='mt-2 space-y-0.5 text-[11px] text-slate-400'>
                                        {signer.sentAt && <p>Sent · {new Date(signer.sentAt).toLocaleString()}</p>}
                                        {signer.viewedAt && <p>Viewed · {new Date(signer.viewedAt).toLocaleString()}</p>}
                                        {signer.completedAt && <p>Completed · {new Date(signer.completedAt).toLocaleString()}</p>}
                                        {!signer.sentAt && !signer.viewedAt && !signer.completedAt && <p>No activity yet</p>}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className='py-6 text-center text-xs text-slate-400'>No signer timeline available.</p>
                        )}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default DocumentViewPage;